import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Library, Plus, Sparkles, Pencil, Eye, Trash2, Loader2, FileQuestion, Play } from "lucide-react";

export default function MeusQuizzes() {
  const [quizzes, setQuizzes] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/quizzes").then((r) => setQuizzes(r.data)).catch(() => setQuizzes([]));
  useEffect(() => { load(); }, []);

  const startGame = async (quizId, count) => {
    if (!count) { toast.error("Adicione questões antes de iniciar o jogo."); return; }
    try {
      const res = await api.post("/games/start", { quiz_id: quizId });
      navigate(`/apresentar/${res.data.session_id}`, { state: res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar o jogo.");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/quizzes/${id}`);
      toast.success("Quiz excluído.");
      load();
    } catch {
      toast.error("Erro ao excluir.");
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center">
            <Library className="w-6 h-6 text-brand-yellow" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-black text-brand-ink">Meus quizzes</h1>
            <p className="text-brand-muted text-sm">Sua biblioteca pessoal</p>
          </div>
        </div>
        <Link to="/quiz/novo">
          <Button className="rounded-full gap-2 bg-brand-blue hover:bg-blue-700"><Plus className="w-4 h-4" /> Novo</Button>
        </Link>
      </div>

      {!quizzes ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-brand-blue" /></div>
      ) : quizzes.length === 0 ? (
        <div className="mt-16 text-center">
          <div className="w-20 h-20 rounded-3xl bg-white shadow-sm border border-slate-100 flex items-center justify-center mx-auto">
            <FileQuestion className="w-10 h-10 text-slate-300" />
          </div>
          <h3 className="mt-6 font-heading font-bold text-xl text-brand-ink">Nenhum quiz ainda</h3>
          <p className="mt-2 text-brand-muted">Crie seu primeiro quiz com IA em segundos.</p>
          <Link to="/quiz/ia">
            <Button className="mt-6 rounded-full gap-2 bg-brand-purple hover:bg-purple-700"><Sparkles className="w-4 h-4" /> Criar com IA</Button>
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {quizzes.map((q) => (
            <div key={q.quiz_id} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow" data-testid={`quiz-item-${q.quiz_id}`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${q.status === "published" ? "bg-emerald-50 text-brand-green" : "bg-slate-100 text-brand-muted"}`}>
                  {q.status === "published" ? "Publicado" : "Rascunho"}
                </span>
                <span className="text-xs text-brand-muted">{q.question_count} questões</span>
              </div>
              <h3 className="mt-4 font-heading font-bold text-lg text-brand-ink line-clamp-2 min-h-[3.5rem]">{q.title}</h3>
              <p className="text-sm text-brand-muted">{q.subject || "Sem disciplina"} {q.grade ? `· ${q.grade}` : ""}</p>
              {q.turma && <span className="mt-2 inline-block text-xs font-semibold text-brand-purple bg-purple-50 px-2.5 py-1 rounded-full" data-testid={`turma-${q.quiz_id}`}>Turma: {q.turma}</span>}
              <Button onClick={() => startGame(q.quiz_id, q.question_count)} className="mt-4 w-full rounded-full gap-2 bg-brand-green hover:bg-emerald-600 h-10" data-testid={`start-${q.quiz_id}`}>
                <Play className="w-4 h-4" /> Iniciar jogo ao vivo
              </Button>
              <div className="mt-3 flex gap-2">
                <Link to={`/quiz/${q.quiz_id}/editar`} className="flex-1">
                  <Button variant="outline" className="w-full rounded-full gap-1.5 h-9" data-testid={`edit-${q.quiz_id}`}><Pencil className="w-3.5 h-3.5" /> Editar</Button>
                </Link>
                <Link to={`/quiz/${q.quiz_id}/visualizar`}>
                  <Button variant="outline" size="icon" className="rounded-full h-9 w-9"><Eye className="w-4 h-4" /></Button>
                </Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-full h-9 w-9 text-red-500 hover:bg-red-50" data-testid={`delete-${q.quiz_id}`}><Trash2 className="w-4 h-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Excluir quiz?</AlertDialogTitle>
                      <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => del(q.quiz_id)} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
